import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import { AnyAction, Store } from 'redux';
import { getSubmitFeedbackStatusFromAction } from '../../JobsScreen/components/JobDetails/JobDetails.submitFeedback';

export type SubmitOutcome = 'success' | 'pending' | 'error';

// Waits for the server's verdict on the submitted results instead of assuming success: the jobs
// saga answers SEND_METER_TASK_RESULTS with DONE (accepted), ERROR (rejected, e.g. a value the
// backend refuses) or QUEUE_ACTION (offline, sent later). Showing the done panel right after the
// dispatch made a rejected send appear as "Erfolgreich" next to "Senden fehlgeschlagen" (qa#128).
// Same action mapping as the manual flow (JobDetails.submitFeedback.ts).
export const useSubmitOutcome = (onOutcome: (outcome: SubmitOutcome) => void): { sending: boolean; track: (jobId: string) => void } => {
    const store = useStore() as Store<any> & { lastAction?: AnyAction | null };
    const [sending, setSending] = useState(false);
    const trackedJobIdRef = useRef<string | undefined>(undefined);
    const onOutcomeRef = useRef(onOutcome);
    onOutcomeRef.current = onOutcome;

    useEffect(
        () =>
            store.subscribe(() => {
                const jobId = trackedJobIdRef.current;
                const lastAction = store.lastAction;
                if (!jobId || !lastAction) {
                    return;
                }
                const outcome = getSubmitFeedbackStatusFromAction(jobId, lastAction);
                if (!outcome) {
                    return;
                }
                trackedJobIdRef.current = undefined;
                setSending(false);
                onOutcomeRef.current(outcome);
            }),
        [store],
    );

    // Call right BEFORE dispatching the send action — the saga may answer synchronously.
    const track = useCallback((jobId: string) => {
        trackedJobIdRef.current = jobId;
        setSending(true);
    }, []);

    return { sending, track };
};
